import { FiArrowLeft, FiUser } from "react-icons/fi";
import backendApi from "../api/backendApi.js";
import { useAuth } from "../api/AuthContext";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import LoadingButton from "../components/LoadingButton";
import AlertModal from "../components/modal/AlertModal";
import { protectedTheme } from "../components/ui/protectedTheme";
import {
  getStates,
  getCities,
  getPostcodes,
  getPostcodesByPrefix,
  findPostcode,
} from "malaysia-postcodes";

function formatDateForInput(d) {
  if (!d) return "";
  const date = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return d;
  return date.toISOString().slice(0, 10);
}

const UserPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    phone: "",
    email: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zip: "",
    aimsNo: "",
    muqami: "",
  });

  const [loading, setLoading] = useState(false);

  // Alert modal state
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertCfg, setAlertCfg] = useState({
    title: "",
    message: "",
    isOneButton: true,
    isSuccess: false,
    isDefault: false,
  });

  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [zipOptions, setZipOptions] = useState([]);

  // Load user into form
  useEffect(() => {
    if (user) {
      setForm({
        firstName: user.firstName ?? "",
        lastName: user.lastName ?? "",
        dateOfBirth: formatDateForInput(user.dateOfBirth),
        phone: user.phone ?? "",
        email: user.email ?? "",
        addressLine1: user.addressLine1 ?? "",
        addressLine2: user.addressLine2 ?? "",
        city: user.city ?? "",
        state: user.state ?? "",
        zip: user.zip ?? "",
        aimsNo: user.aimsNo ?? "",
        muqami: user.muqami ?? "",
      });
    }
  }, [user]);

  useEffect(() => {
    setStateOptions(getStates().sort());
  }, []);

  useEffect(() => {
    if (!user) return;

    const existingZip = user.zip ? String(user.zip) : "";
    if (existingZip) {
      const res = findPostcode(existingZip, true);
      if (res?.found) {
        const derivedState = res.state;
        const derivedCity = res.city;

        setForm((f) => ({
          ...f,
          state: derivedState || f.state || "",
          city: derivedCity || f.city || "",
          zip: existingZip,
        }));

        if (derivedState) setCityOptions(getCities(derivedState));
        if (derivedState && derivedCity)
          setZipOptions(getPostcodes(derivedState, derivedCity));
        return;
      }
    }

    // Fallback path
    if (user?.state) {
      setCityOptions(getCities(user.state));
    }
    if (user?.state && user?.city) {
      setZipOptions(getPostcodes(user.state, user.city));
    }
  }, [user]);

  const showSuccess = (message) => {
    setAlertCfg({
      title: "Profile Updated",
      message,
      isOneButton: true,
      isSuccess: true,
      isDefault: false,
    });
    setAlertOpen(true);
  };

  const showError = (message) => {
    setAlertCfg({
      title: "Update Failed",
      message,
      isOneButton: true,
      isSuccess: false,
      isDefault: false,
    });
    setAlertOpen(true);
  };

  const onChange = (e) => {
    const { name, value } = e.target;

    // Base update
    setForm((f) => ({ ...f, [name]: value }));

    if (name === "state") {
      const newCities = value ? getCities(value) : [];
      setCityOptions(newCities);
      setZipOptions([]);
      setForm((f) => ({ ...f, city: "", zip: "" }));
    }

    if (name === "city") {
      const zips = form.state && value ? getPostcodes(form.state, value) : [];
      setZipOptions(zips);
      setForm((f) => ({ ...f, zip: "" }));
    }

    if (name === "zip") {
      const prefix = value.replace(/\D/g, "");
      if (prefix.length >= 1) {
        // suggest by prefix while typing
        setZipOptions(getPostcodesByPrefix(prefix));
      } else {
        if (form.state && form.city) {
          setZipOptions(getPostcodes(form.state, form.city));
        } else {
          setZipOptions([]);
        }
      }

      // On 5-digit completion, reverse lookup
      if (/^\d{5}$/.test(prefix)) {
        const exact = findPostcode(prefix, true);
        if (exact?.found) {
          const derivedState = exact.state;
          const derivedCity = exact.city;

          if (derivedState && derivedState !== form.state) {
            setForm((f) => ({ ...f, state: derivedState }));
            setCityOptions(getCities(derivedState));
          }
          if (derivedCity && derivedCity !== form.city) {
            setForm((f) => ({ ...f, city: derivedCity }));
            setZipOptions(getPostcodes(derivedState, derivedCity));
          }
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      showError("You are not signed in. Please log in and try again.");
      return;
    }

    setLoading(true);
    try {
      await backendApi.put("/users/me", form);
      showSuccess("Your profile information has been saved.");
    } catch (err) {
      showError("Unexpected error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={protectedTheme.page}>
      <div className="mx-auto w-full max-w-4xl">
      <div className={protectedTheme.card}>
        {/* Back button and title */}
        <div className="flex items-center mb-6 md:hidden">
          <button
            className="mr-4 text-2xl text-emerald-800"
            onClick={() => navigate("/profile")}
          >
            <FiArrowLeft />
          </button>
          <h2 className="text-xl font-black text-zinc-950">Edit Profile</h2>
        </div>

        {/* Profile Picture */}
        <div className="flex flex-col items-center mb-6">
          <p className="hidden text-sm font-semibold uppercase tracking-[0.2em] text-emerald-700 md:block">
            Account Details
          </p>
          <h1 className="mb-5 hidden text-3xl font-black text-zinc-950 md:block">
            Edit Profile
          </h1>
          <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-full bg-emerald-950 text-lime-200">
            <FiUser className="text-5xl" />
          </div>
          <p className="mt-4 text-lg font-black text-zinc-950">{user?.firstName}</p>
        </div>

        <form className="space-y-4 flex-col" onSubmit={handleSubmit}>
          {/* First Name */}
          <div>
            <label className={protectedTheme.label}>First Name</label>
            <input
              name="firstName"
              type="text"
              className={`mt-1 ${protectedTheme.input}`}
              value={form.firstName}
              onChange={onChange}
              required
            />
          </div>

          {/* Last Name */}
          <div>
            <label className={protectedTheme.label}>Last Name</label>
            <input
              name="lastName"
              type="text"
              className={`mt-1 ${protectedTheme.input}`}
              value={form.lastName}
              onChange={onChange}
              required
            />
          </div>

          {/* Date of Birth */}
          <div>
            <label className={protectedTheme.label}>Date of Birth</label>
            <input
              name="dateOfBirth"
              type="date"
              className={`mt-1 ${protectedTheme.input}`}
              value={form.dateOfBirth}
              required
              onChange={onChange}
            />
          </div>

          {/* Phone */}
          <div>
            <label className={protectedTheme.label}>Phone Number</label>
            <input
              name="phone"
              type="tel"
              className={`mt-1 ${protectedTheme.input}`}
              placeholder="+60 12-345 6789"
              value={form.phone}
              required
              onChange={onChange}
            />
          </div>

          {/* Email */}
          <div>
            <label className={protectedTheme.label}>Email</label>
            <input
              name="email"
              type="email"
              className={`mt-1 ${protectedTheme.input}`}
              placeholder="you@example.com"
              value={form.email}
              required
              disabled
            />
          </div>

          {/* Address Line 1 */}
          <div>
            <label className={protectedTheme.label}>
              Address Line 1
            </label>
            <input
              name="addressLine1"
              type="text"
              className={`mt-1 ${protectedTheme.input}`}
              value={form.addressLine1}
              onChange={onChange}
              required
            />
          </div>

          {/* Address Line 2 */}
          <div>
            <label className={protectedTheme.label}>
              Address Line 2
            </label>
            <input
              name="addressLine2"
              type="text"
              className={`mt-1 ${protectedTheme.input}`}
              value={form.addressLine2}
              onChange={onChange}
            />
          </div>

          {/* City, State, Zip (cascading) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* State */}
            <div>
              <label className={protectedTheme.label}>State</label>
              <select
                name="state"
                className={`mt-1 ${protectedTheme.input}`}
                value={form.state}
                onChange={onChange}
                required
              >
                <option value="">Select…</option>
                {stateOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            {/* City (type-ahead via datalist) */}
            <div>
              <label className={protectedTheme.label}>City</label>
              <input
                list="city-list"
                name="city"
                type="text"
                className={`mt-1 ${protectedTheme.input}`}
                value={form.city}
                onChange={onChange}
                placeholder={
                  form.state ? "Start typing city…" : "Select state first"
                }
                disabled={!form.state}
                autoComplete="off"
                required
              />
              <datalist id="city-list">
                {cityOptions.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>

            {/* ZIP / Postcode (type-ahead + reverse lookup) */}
            <div>
              <label className={protectedTheme.label}>ZIP</label>
              <input
                list="zip-list"
                name="zip"
                type="text"
                className={`mt-1 ${protectedTheme.input}`}
                value={form.zip}
                onChange={onChange}
                placeholder={
                  form.city
                    ? "Type postcode (5 digits)…"
                    : form.state
                    ? "Choose city first"
                    : "Choose state first"
                }
                disabled={!form.state}
                autoComplete="off"
                inputMode="numeric"
                pattern="\d*"
                required
              />
              <datalist id="zip-list">
                {zipOptions.map((z) => (
                  <option key={z} value={z} />
                ))}
              </datalist>
            </div>
          </div>

          {/* AIMS No */}
          <div>
            <label className={protectedTheme.label}>AIMS No</label>
            <input
              name="aimsNo"
              type="text"
              className={`mt-1 ${protectedTheme.input}`}
              value={form.aimsNo}
              required
              onChange={onChange}
            />
          </div>

          {/* Muqami */}
          <div>
            <label className={protectedTheme.label}>Muqami</label>
            <input
              name="muqami"
              type="text"
              className={`mt-1 ${protectedTheme.input}`}
              value={form.muqami}
              required
              disabled
            />
          </div>

          <LoadingButton type="submit" loading={loading}>
            Save
          </LoadingButton>
        </form>
      </div>
      </div>

      <AlertModal
        isOpen={alertOpen}
        onClose={() => setAlertOpen(false)}
        title={alertCfg.title}
        message={alertCfg.message}
        isOneButton={alertCfg.isOneButton}
        isSuccess={alertCfg.isSuccess}
        isDefault={alertCfg.isDefault}
        onSuccessOneButton={() => {
          window.location.reload();
          setAlertOpen(false);
        }}
      />
    </div>
  );
};

export default UserPage;
